import React from 'react';
import PropTypes from 'prop-types';
import styles from '../ResultCard/styles/styles.module.scss';

import AddRemoveButton from '../../shared/AddRemoveButton';
import CardImage from '../CardImage';
import CardImageCarousel from '../CardImageCarousel';
import CardLink from '../CardLink';
import CompletedBadge from '../../shared/badges/CompletedBadge/CompletedBadge';
import FavoriteButton from '../../shared/FavoriteButton';
import MoveHandle from '../../shared/MoveHandle';
import TrailCardContent from '../TrailCardContent';
import { getTrailProfilePhotoUrl, getFormattedTrailUrls, getTrailPhotoUrls, getLocalizedName } from '../../../utils/trail_helpers';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { shallowCompare } from '../../../utils/compare';
import { getAmplitudeInstance } from '../../../utils/amplitude_helpers';
import { TRAIL_CARD_ADD_TO_LIST_BUTTON_TAPPED } from '../../../utils/constants/AmplitudeEvent';
import TrailPropType from '../../../proptypes/TrailPropType';
import AlgoliaTrailPropType from '../../../proptypes/AlgoliaTrailPropType';

class TrailResultCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      photoUrls: this.getInitPhotoUrls(props.trail),
      preloaded: false,
      focused: false
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState, propKey => !propKey.includes('Click'));
  }

  getInitPhotoUrls(trail) {
    let numPhotos = trail.num_photos || 0;
    if (numPhotos > this.props.maxInitPhotos) {
      numPhotos = this.props.maxInitPhotos;
    }
    const photoUrls = [getTrailProfilePhotoUrl(trail, 'large')];
    for (let i = 1; i < numPhotos; i++) {
      // Initialize React slick carousel with CardImages that don't have an image (e.g. just a background gradient)
      photoUrls.push(null);
    }

    return photoUrls;
  }

  handleMouseEnter = () => {
    // Preload image carousel photos on first hover
    if (!this.state.preloaded) {
      this.preloadPhotos();
    }

    if (this.props.messagingChannel) {
      this.props.messagingChannel.publish('item.mouseenter', this.props.trail);
    }
  };

  handleMouseLeave = () => {
    if (this.props.messagingChannel) {
      this.props.messagingChannel.publish('item.mouseleave', this.props.trail);
    }
  };

  handleBeforeImageChange = () => {
    // Preload image carousel photos on first change (for mobile, because mouseEnter isn't fired on mobile)
    if (!this.state.preloaded) {
      this.preloadPhotos();
    }
  };

  handleCardClick = () => {
    if (this.props.ignoreClicks) {
      return false;
    }
    // TODO: Move these outside of component?
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'Search Page', 'Result List', `Position ${this.props.idx} Trail Clicked`]);
    ServerCommunicationUtil.handleGaqPush.bind(null, ['_trackEvent', 'Search Page', 'Hovercard', 'Trail Click']);
    if (this.props.handleCardClick) {
      this.props.handleCardClick();
    } else if (this.props.trailUrl) {
      window.location = this.props.trailUrl;
    } else {
      const { localizedUrl } = getFormattedTrailUrls(this.props.trail, this.props.context);
      window.location = localizedUrl;
    }
    return false;
  };

  toggleLinkFocus = focused => {
    if (window.keyboardUser) {
      this.setState({ focused });
    }
  };

  preloadPhotos() {
    if (!this.props.trail.num_photos) {
      return;
    }
    getTrailPhotoUrls(this.props.trail, this.props.maxPhotos, 'large')
      .then(loadedUrls => {
        let { photoUrls } = this.state;
        loadedUrls = loadedUrls.slice(1); // remove profile photo
        // Combine pre-loaded profile photo url with loadedUrls (prevent image flash by keeping url the same)
        photoUrls = [photoUrls[0], ...loadedUrls];
        // hack to pre-load images by browser
        loadedUrls.forEach(photoUrl => {
          new Image().src = photoUrl;
        });
        this.setState({ preloaded: true, photoUrls });
      })
      .catch(e => {
        // catch if this errors so development doesn't crash
        console.error('<TrailResultsCard />, getTrailPhotosUrl', e);
      });
  }

  renderTopLeftButton() {
    if (this.props.allowItemMoving) {
      return <MoveHandle className={styles.topLeftButton} dragHandleProps={this.props.dragHandleProps} />;
    }
    return (
      <CompletedBadge
        className={styles.badge}
        isCompleted={this.props.isCompleted}
        isVerified={this.props.isVerified}
        handleClick={this.props.allowClickingCompletedBadge ? this.props.handleCompletedBadgeClick : null}
      />
    );
  }

  renderTopRightButton() {
    if (this.props.allowItemDelete && this.props.handleItemDeleteClick) {
      return (
        <AddRemoveButton
          className={styles.topRightButton}
          handleClick={this.props.handleItemDeleteClick}
          isInCollection
          removeLabel={this.props.itemDeleteLabel}
        />
      );
    }
    if (this.props.showFavoriteHeart && this.props.handleFavoriteClick) {
      return (
        <FavoriteButton
          className={styles.topRightButton}
          handleFavoriteClick={e => {
            getAmplitudeInstance().logEvent(TRAIL_CARD_ADD_TO_LIST_BUTTON_TAPPED, { 'trail id': this.props.trail.ID });
            this.props.handleFavoriteClick(e);
          }}
          isFavorite={this.props.isFavorite}
          onWhiteBg={this.props.descriptive}
        />
      );
    }
    return null;
  }

  renderCardImage() {
    const { opaqueOverlay, trail } = this.props;
    const { photoUrls } = this.state;
    if (!photoUrls) {
      return null;
    }

    let profilePhotoUrl = photoUrls[0];
    if (trail.profile_photo_data) {
      const [photoId, photoHash] = trail.profile_photo_data.split('-');
      profilePhotoUrl = `https://cdn-assets.alltrails.com/uploads/photo/image/${photoId}/large_${photoHash}.jpg`;
    }

    if (photoUrls.length > 1 && !opaqueOverlay) {
      return (
        <CardImageCarousel
          className={`${styles.imageCarousel} ${this.props.imageCarouselClassName}`}
          classNameImage={`${styles.image} ${this.props.imageCarouselClassName}`}
          handleBeforeChange={this.handleBeforeImageChange}
          handleCardClick={this.handleCardClick}
          lowerButtons={!this.props.descriptive}
          photoUrls={photoUrls}
          title={this.props.trail.name}
        />
      );
    }
    return (
      <CardImage
        className={`${styles.imageCarousel} ${this.props.imageCarouselClassName}`}
        lazyload
        url={profilePhotoUrl}
        title={trail.name}
        darker={opaqueOverlay}
      />
    );
  }

  render() {
    const classes = [this.props.descriptive ? styles.containerDescriptive : styles.container];
    if (this.props.className) {
      classes.push(this.props.className);
    }

    if (this.state.focused) {
      classes.push(styles.focused);
    }

    let { url: trailSchemaId, localizedUrl } = getFormattedTrailUrls(this.props.trail, this.props.context);

    // Note: trailSchemaId is trail's url BEFORE having its url wrapped
    if (trailSchemaId.indexOf('?') > 0) {
      trailSchemaId = trailSchemaId.substring(0, trailSchemaId.indexOf('?'));
    }

    if (this.props.trailUrl) {
      localizedUrl = this.props.trailUrl;
    }

    if (localizedUrl.indexOf('?') < 0) {
      localizedUrl = `${localizedUrl}?ref=${this.props.linkRef}`;
    }

    return (
      <div
        className={classes.join(' ')}
        data-id={this.props.trail.ID}
        itemID={trailSchemaId}
        itemScope
        itemType="http://schema.org/LocalBusiness"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <CardLink
          url={localizedUrl}
          title={getLocalizedName(this.props.trail, this.props.context.languageRegionCode)}
          handleClick={this.handleCardClick}
          toggleLinkFocus={this.toggleLinkFocus}
        />
        {this.renderCardImage()}
        <TrailCardContent
          className={styles.content}
          context={this.props.context}
          descriptive={this.props.descriptive}
          displayRank={this.props.displayRank}
          forceCityLink={this.props.forceCityLink}
          handleLocationClick={this.props.handleLocationClick}
          namePaddingRight={this.props.descriptive ? 24 : 0}
          trail={this.props.trail}
          url={localizedUrl}
        />
        {this.renderTopLeftButton()}
        {this.renderTopRightButton()}
      </div>
    );
  }
}

TrailResultCard.propTypes = {
  handleFavoriteClick: PropTypes.func.isRequired,
  imageCarouselClassName: PropTypes.string,
  linkRef: PropTypes.string,
  maxInitPhotos: PropTypes.number,
  maxPhotos: PropTypes.number,
  showFavoriteHeart: PropTypes.bool,
  trail: AlgoliaTrailPropType.isRequired
};

TrailResultCard.defaultProps = {
  maxInitPhotos: 5, // how many to display before pre-load
  maxPhotos: 6, // how many to display after pre-load
  showFavoriteHeart: false,
  imageCarouselClassName: '',
  linkRef: 'result-card'
};

export default TrailResultCard;
